import { ChannelSpace, handleDeviceDetect } from 'src/provider/ChannelContextProvider'
import { handleJsonParse } from 'src/utils/index'
import { regExps } from 'src/utils/regExps_util'
import { parseBridgeJson, withBridgeTracking } from 'src/utils/sentry/bridgeTracking'

const { channel, ios, aos }: ChannelSpace.ChannelContextValue = handleDeviceDetect()
const voidFn = () => {}

// 네이밍 규칙
//  web=>aos  interface SaturnWebToAosBridge  응답이 있는 요청 get
//  web=>ios interface SaturnWebToIosBridge  응답이 있는 요청 get
//  web=>aos  interface SaturnWebToAosBridge  요청만 있는 경우 handleReq
//  web=>ios interface SaturnWebToIosBridge  요청만 있는 경우 handleReq
//  aos=>web  interface SaturnAosToWebBridge  요청에 대한 응답 receive
//  ios=>web  interface SaturnIosToWebBridge  요청에 대한 응답 receive

// 사용자 현재 위치 받아오는 브릿지 함수
const getLocationInfo = {
  aos: {
    req: () => window.SaturnAosInterface.getLocationInfo(),
    res: (callback?: ({ lat, lng }: { lat: number; lng: number }) => void) => {
      const clickHandler = function () {
        window.SaturnWebInterface = {
          ...window.SaturnWebInterface,
          handleLocationInfo: (info: string) => {
            callback?.({ lat: Number(info?.split(',')?.[0]), lng: Number(info?.split(',')?.[1]) })
          },
        }
        window.removeEventListener('click', clickHandler)
      }
      window.addEventListener('click', clickHandler)
    },
  },
  ios: {
    req: () => window.webkit.messageHandlers.SaturnIosInterface.postMessage('getLocationInfo'),
    res: (callback?: ({ lat, lng }: { lat: number; lng: number }) => void) => {
      const clickHandler = function () {
        window.SaturnIosInterface = {
          ...window.SaturnIosInterface,
          receiveLocationInfo: (value: string) => {
            const { latitude, longitude } = parseBridgeJson(value)
            if (latitude === 'null' || longitude === 'null') {
              setTimeout(() => {
                Bridge.getLocationInfo.req()
              }, 1000)
            } else {
              callback?.({ lat: latitude, lng: longitude })
            }
          },
        }
        window.removeEventListener('click', clickHandler)
      }
      window.addEventListener('click', clickHandler)
    },
  },
  web: { req: voidFn, res: voidFn },
}

// 사용자 위치권한 여부 브릿지 함수
const getLocationPermission = {
  aos: {
    req: () => window.SaturnAosInterface.getLocationPermission(),
    res: voidFn,
  },
  ios: {
    req: () =>
      window.webkit.messageHandlers.SaturnIosInterface.postMessage('getLocationPermission'),
    res: () => {
      const clickHandler = function () {
        window.SaturnIosInterface = {
          ...window.SaturnIosInterface,
          receiveLocationPermissionStatus: (value: string) => {
            const isPermission = value === 'true'
            isPermission ? Bridge.getLocationInfo.req() : Bridge.getLocationPermissionShow.req()
          },
        }
        window.removeEventListener('click', clickHandler)
      }
      window.addEventListener('click', clickHandler)
    },
  },
  web: { req: voidFn, res: voidFn },
}

const getLocationPermissionShow = {
  aos: {
    req: () => window.SaturnAosInterface.getLocationPermShow(),
    res: voidFn,
  },
  ios: {
    req: () => window.webkit.messageHandlers.SaturnIosInterface.postMessage('getLocationPermShow'),
    res: (callback?: () => void) => {
      const clickHandler = function () {
        window.SaturnIosInterface = {
          ...window.SaturnIosInterface,
          receiveLocationPermShow: (value: string) => {
            const isPermShow = value === 'true'
            isPermShow ? Bridge.handleReqLocationPermission() : callback?.()
          },
        }
        window.removeEventListener('click', clickHandler)
      }
      window.addEventListener('click', clickHandler)
    },
  },
  web: { req: voidFn, res: voidFn },
}

//푸시토큰 브릿지 함수
const getPushToken = {
  aos: {
    req: () => window.SaturnAppBridge.getPushToken(),
    res: (callback?: (pushToken: string) => void) => {
      const clickHandler = function () {
        window.SaturnWebInterface = {
          ...window.SaturnWebInterface,
          handlePushToken: (pushToken: string) => callback?.(pushToken),
        }
        window.removeEventListener('click', clickHandler)
      }
      window.addEventListener('click', clickHandler)
    },
  },
  ios: {
    req: () => window.webkit.messageHandlers.SaturnIosInterface.postMessage('getPushToken'),
    res: (callback?: (pushToken: string) => void) => {
      const clickHandler = function () {
        window.SaturnIosInterface = {
          ...window.SaturnIosInterface,
          receivePushToken: (pushToken: string) => callback?.(pushToken),
        }
        window.removeEventListener('click', clickHandler)
      }
      window.addEventListener('click', clickHandler)
    },
  },
  web: { req: voidFn, res: voidFn },
}

// 앱 버전, 모델등을 가져오는 브릿지 함수
const getAppInfo = {
  aos: {
    req: (callback?: (appVersion: string) => void) => {
      if (window?.SaturnAosInterface !== undefined) {
        const parseData = handleJsonParse(window?.SaturnAosInterface?.getAppInfo())
        const { appVersion, modelName } = parseData
        callback?.(`${appVersion}`)
      } else {
        callback?.('previous_version')
        console.warn('버전을 받아올 수 없음')
      }
    },
    res: voidFn,
  },
  ios: {
    req: () => window.webkit.messageHandlers.SaturnIosInterface.postMessage('getAppInfo'),
    res: (callback?: (appVersion: string) => void) => {
      window.SaturnIosInterface = {
        ...window.SaturnIosInterface,
        receiveAppInfo: (appInfo: string) => {
          const parseData = handleJsonParse(appInfo)
          const { appVersion, modelName } = parseData
          callback?.(`${appVersion}`)
        },
      }
    },
  },
  web: {
    req: voidFn,
    res: voidFn,
  },
}

// 직접쿠폰등록 이미지 업로드
const getUploadImage = {
  aos: {
    req: () => window.SaturnAosInterface.selectImage(),
    res: (callback?: (result: string) => void) => {
      const clickHandler = function () {
        window.SaturnWebInterface = {
          ...window.SaturnWebInterface,
          handleSelectedImg: (result) => callback?.(result),
        }
        window.removeEventListener('click', clickHandler)
      }
      window.addEventListener('click', clickHandler)
    },
  },
  ios: {
    req: () => window.webkit.messageHandlers.SaturnIosInterface.postMessage('selectImage'),
    res: (callback?: (result: string) => void) => {
      const clickHandler = function () {
        window.SaturnIosInterface = {
          ...window.SaturnIosInterface,
          receiveSelectedImg: (result) => callback?.(result),
        }
        window.removeEventListener('click', clickHandler)
      }
      window.addEventListener('click', clickHandler)
    },
  },
  web: { req: voidFn, res: voidFn },
}

// 외부링크 브릿지 함수
const handleReqExternalOpenUrl = {
  aos: (url: string) => {
    if (window?.SaturnAosInterface !== undefined) {
      window.SaturnAosInterface.handleExternalOpenUrl(url)
    } else {
      window.SaturnAppInterface.handleExternalOpenUrl(url)
    }
  },
  ios: (url: string) =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage(`handleExternalOpenUrl(${url})`),
  web: (url: string) => window.open(url),
}

// 토스트 메시지 호출 브릿지 함수
const handleReqShowToast = {
  aos: (msg?: string, callback?: () => void) => window.SaturnAosInterface.showToast(msg as string),
  ios: (msg?: string, callback?: () => void) => callback?.(),
  web: (msg?: string, callback?: () => void) => callback?.(),
}

// 이미지 저장 브릿지 함수
const handleReqSaveImage = {
  aos: (base64String?: string, callback?: () => void) =>
    window.SaturnAosInterface.saveImage(base64String as string),
  ios: (base64String?: string, callback?: () => void) =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage(`saveImage(${base64String})`),
  web: (base64String?: string, callback?: () => void) => callback?.(),
}

// 앱 종료 브릿지 함수
const handleReqAppClose = {
  aos: () => window.SaturnAppBridge.handleAppClose(),
  ios: () => window.webkit.messageHandlers.SaturnIosInterface.postMessage('handleAppClose'),
  web: voidFn,
}

// 앱 밝기 상향 브릿지 함수
const handleReqUpDisplayLight = {
  aos: () => window.SaturnAppBridge.handleUpDisplayLight(),
  ios: () => window.webkit.messageHandlers.SaturnIosInterface.postMessage('handleUpDisplayLight'),
  web: voidFn,
}

// 앱 밝기 하향 브릿지 함수
const handleReqDownDisplayLight = {
  aos: () => window.SaturnAppBridge.handleDownDisplayLight(),
  ios: () => window.webkit.messageHandlers.SaturnIosInterface.postMessage('handleDownDisplayLight'),
  web: voidFn,
}

// 알림 시스템 설정 페이지 보내는 브릿지 함수
const handleReqSystemNotification = {
  aos: () => window.SaturnAppBridge.handleReqSystemNotification(),
  ios: () =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage('handleReqSystemNotification'),
  web: voidFn,
}

const handleReqLocationPermission = {
  aos: () => window.SaturnAppBridge.handleReqLocationPermission(),
  ios: () =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage('handleReqLocationPermission'),
  web: voidFn,
}

const handleReqSystemLocation = {
  aos: () => window.SaturnAppBridge.handleReqSystemLocation(),
  ios: () =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage('handleReqSystemLocation'),
  web: voidFn,
}

const handleReqNotification = {
  aos: () => window.SaturnAppBridge.handleReqNotification(),
  ios: voidFn,
  web: voidFn,
}

// 앱에서 실행시 백그라운드 splash화면에서 url 이동 함수
const handleWebViewStartUrl = {
  aos: (url: string, pNumber: string) => {
    try {
      window.SaturnAosInterface.handleWebViewStartUrl(url, pNumber)
    } catch (e) {
      console.warn(`웹뷰 시작 브릿지 호출 실패: ${e}`)
    }
  },
  ios: (url: string, pNumber: string) => {
    window.webkit.messageHandlers.SaturnIosInterface.postMessage(
      `handleWebViewStartUrl(${url}, ${pNumber})`,
    )
  },
  web: voidFn,
}

// 간편로그인 화면 호출
const handleReqEntryEasyLogin = {
  aos: (pNumber: string) => {
    try {
      window.SaturnAosInterface.reqEntryEasyLogin(pNumber)
    } catch (e) {
      console.warn(`간편로그인 화면 호출 실패: ${e}`)
    }
  },
  ios: (pNumber: string) =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage(`reqEntryEasyLogin(${pNumber})`),
  web: voidFn,
}

// 마이페이지 간편로그인 설정
const handleReqSetEasyLogin = {
  aos: (pNumber: string) => {
    try {
      window.SaturnAosInterface.reqSetEasyLogin(pNumber)
    } catch (e) {
      console.warn(`마이페이지 간편로그인 설정 화면 호출 실패: ${e}`)
    }
  },
  ios: (pNumber: string) =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage(`reqSetEasyLogin(${pNumber})`),
  web: voidFn,
}

// 초기 간편로그인 설정
const handleReqSignUpLogin = {
  aos: (pNumber: string, biometricAgree: string) => {
    try {
      try {
        window.SaturnAosInterface.reqSignUpLogin(pNumber, biometricAgree)
      } catch (e) {
        console.warn(`초기 간편로그인 설정 화면 호출 실패: biometricAgree`)
        window.SaturnAosInterface.reqSignUpLogin(pNumber)
      }
    } catch (e) {
      console.warn(`초기 간편로그인 설정 화면 호출 실패: ${e}`)
    }
  },
  ios: (pNumber: string, biometricAgree: string) =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage(
      `reqSignUpLogin(${pNumber}${!!biometricAgree ? `, ${biometricAgree}` : ''})`,
    ),
  web: voidFn,
}

// PIN 5회 오류
const receiveResetPinSession = {
  aos: (callback?: () => void) => {
    window.SaturnWebInterface = {
      ...window.SaturnWebInterface,
      receiveResetPinSession: () => callback?.(),
    }
  },
  ios: (callback?: () => void) => {
    window.SaturnIosInterface = {
      ...window.SaturnIosInterface,
      receiveResetPinSession: () => callback?.(),
    }
  },
  web: voidFn,
}

// 웹->앱 인증된 이름 저장
const handleReqSaveName = {
  aos: (name: string) => {
    try {
      window.SaturnAosInterface.handleSendNameToApp(name)
    } catch (e) {
      console.warn(`웹->앱 인증된 이름 저장 요청 실패: ${e}`)
    }
  },
  ios: (name: string) =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage(`handleSendNameToApp(${name})`),
  web: voidFn,
}

// 웹->앱 저장된 이름 갖고오기
const getUserName = {
  aos: {
    req: (failFn?: () => void) => {
      try {
        window.SaturnAosInterface.handleReqNameFromApp()
      } catch (e) {
        console.warn(`웹->앱 저장된 요청 실패: ${e}`)
        failFn?.()
      }
    },
    res: (callback?: (result: string) => void) => {
      window.SaturnWebInterface = {
        ...window.SaturnWebInterface,
        receiveNameFromApp: (result) => callback?.(result),
      }
    },
  },
  ios: {
    req: () => {
      window.webkit.messageHandlers.SaturnIosInterface.postMessage('handleReqNameFromApp')
    },
    res: (callback?: (result: string) => void) => {
      window.SaturnIosInterface = {
        ...window.SaturnIosInterface,
        receiveNameFromApp: (result) => callback?.(result),
      }
    },
  },
  web: { req: voidFn, res: voidFn },
}

// 생체인증 동의여부 갖고오기
const getBiometricAgreeState = {
  aos: {
    req: (pNumber: string, failFn?: () => void) => {
      try {
        window.SaturnAosInterface.getBiometricAgreeState(pNumber)
      } catch (e) {
        console.warn(`생체인증 동의여부 요청 실패: ${e}`)
        failFn?.()
      }
    },
    res: (callback?: (result: string) => void) => {
      window.SaturnWebInterface = {
        ...window.SaturnWebInterface,
        receiveBiometricAgreeState: (result) => callback?.(result),
      }
    },
  },
  ios: {
    req: (pNumber: string) => {
      window.webkit.messageHandlers.SaturnIosInterface.postMessage(
        `getBiometricAgreeState(${pNumber})`,
      )
    },
    res: (callback?: (result: string) => void, failFn?: () => void) => {
      // 응답 타임아웃 설정 (예: 3초)
      const timeoutFn = setTimeout(() => {
        console.warn('브릿지 응답 타임아웃(구버전)')
        failFn?.()
      }, 3000)

      window.SaturnIosInterface = {
        ...window.SaturnIosInterface,
        receiveBiometricAgreeState: (result) => {
          clearTimeout(timeoutFn)
          callback?.(result)
        },
      }
    },
  },
  web: { req: voidFn, res: voidFn },
}

// 생체인증 동의여부 변경
const handleBiometricAgreeState = {
  aos: {
    req: (pNumber: string, isAgree: string) => {
      try {
        window.SaturnAosInterface.handleBiometricAgreeState(pNumber, isAgree)
      } catch (e) {
        console.warn(`생체인증 동의여부 변경 실패: ${e}`)
      }
    },
    res: (callback?: (result: string) => void) => {
      window.SaturnWebInterface = {
        ...window.SaturnWebInterface,
        handleBiometricAgreeState: (result) => callback?.(result),
      }
    },
  },
  ios: {
    req: (pNumber: string, isAgree: string) =>
      window.webkit.messageHandlers.SaturnIosInterface.postMessage(
        `handleBiometricAgreeState(${pNumber}, ${isAgree})`,
      ),
    res: (callback?: (result: string) => void) => {
      window.SaturnIosInterface = {
        ...window.SaturnIosInterface,
        handleBiometricAgreeState: (result) => callback?.(result),
      }
    },
  },
  web: { req: voidFn, res: voidFn },
}

// 생체인증 요청
const handleReqBiometricAuth = {
  aos: {
    req: () => {
      try {
        window.SaturnAosInterface.reqBiometricAuthenticate()
      } catch (e) {
        console.warn(`생체인증 요청 실패: ${e}`)
      }
    },
    res: (callback?: (result: string) => void) => {
      window.SaturnWebInterface = {
        ...window.SaturnWebInterface,
        receiveBiometricResult: (result) => callback?.(result),
      }
    },
  },
  ios: {
    req: () =>
      window.webkit.messageHandlers.SaturnIosInterface.postMessage(`reqBiometricAuthenticate`),
    res: (callback?: (result: string) => void) => {
      window.SaturnIosInterface = {
        ...window.SaturnIosInterface,
        receiveBiometricResult: (result) => callback?.(result),
      }
    },
  },
  web: { req: voidFn, res: voidFn },
}

const getPNumber = {
  aos: {
    req: () => {
      try {
        window.SaturnAosInterface.handleReqPNumberFromApp()
        return true
      } catch (e) {
        console.warn(`pNumber 요청 실패: ${e}`)
        return false
      }
    },
    res: (callback?: (result: string) => void) => {
      window.SaturnWebInterface = {
        ...window.SaturnWebInterface,
        receivePNum: (result) => {
          const isValid = regExps.telReg.pattern.test(result)
          callback?.(isValid ? result : '')
        },
      }
    },
  },
  ios: {
    req: () => {
      window.webkit.messageHandlers.SaturnIosInterface.postMessage(`handleReqPNumberFromApp`)
      return true
    },
    res: (callback?: (result: string) => void, failFn?: () => void) => {
      // 응답 타임아웃 설정 (예: 3초)
      const timeoutFn = setTimeout(() => {
        console.warn('브릿지 응답 타임아웃(구버전)')
        failFn?.()
      }, 3000)

      window.SaturnIosInterface = {
        ...window.SaturnIosInterface,
        receivePNum: (result) => {
          const isValid = regExps.telReg.pattern.test(result)
          clearTimeout(timeoutFn)
          callback?.(isValid ? result : '')
        },
      }
    },
  },
  web: { req: voidFn, res: voidFn },
}

// 연락처 권한여부 갖고오기
const getContactPermission = {
  aos: () => {
    try {
      return window.SaturnAosInterface.getContactPermission()
    } catch (e) {
      console.warn(`연락처 권한여부 갖고오기 실패: ${e}`)
      return false
    }
  },
  ios: voidFn,
  web: voidFn,
}

// 연락처 권한 요청
const handleReqContactPermission = {
  aos: () => {
    try {
      window.SaturnAppBridge.handleReqContactPermission()
    } catch (e) {
      console.warn(`연락처 권한 요청 실패: ${e}`)
    }
  },
  ios: voidFn,
  web: voidFn,
}

// 연락처 화면호출 및 선택번호 받아오기
const handleContactPicker = {
  aos: {
    req: () => {
      try {
        window.SaturnAosInterface.reqContactPicker()
      } catch (e) {
        console.warn(`연락처 화면 호출 실패: ${e}`)
      }
    },
    res: (callback?: (result: string) => void) => {
      window.SaturnWebInterface = {
        ...window.SaturnWebInterface,
        handleSelectedContact: (result) => callback?.(result),
      }
    },
  },
  ios: {
    req: () => window.webkit.messageHandlers.SaturnIosInterface.postMessage(`reqContactPicker`),
    res: (callback?: (result: string) => void) => {
      window.SaturnIosInterface = {
        ...window.SaturnIosInterface,
        handleSelectedContact: (result) => callback?.(result),
      }
    },
  },
  web: { req: voidFn, res: voidFn },
}

// 앱에 저장된 authkey 요청 및 받아오기
const getAuthKey = {
  aos: {
    req: () => {
      try {
        window.SaturnAosInterface.handleReqAuthKeyFromApp()
        return true
      } catch (e) {
        console.warn(`authkey 요청 실패: ${e}`)
        return false
      }
    },
    res: (callback?: (result: string) => void) => {
      window.SaturnWebInterface = {
        ...window.SaturnWebInterface,
        receiveAuthKeyFromApp: (result) => callback?.(result),
      }
    },
  },
  ios: {
    req: () => {
      window.webkit.messageHandlers.SaturnIosInterface.postMessage(`handleReqAuthKeyFromApp`)
      return true
    },
    res: (callback?: (result: string) => void, failFn?: () => void) => {
      // 응답 타임아웃 설정 (예: 3초)
      const timeoutFn = setTimeout(() => {
        console.warn('브릿지 응답 타임아웃(구버전)')
        failFn?.()
      }, 3000)

      window.SaturnIosInterface = {
        ...window.SaturnIosInterface,
        receiveAuthKeyFromApp: (result) => {
          clearTimeout(timeoutFn)
          callback?.(result)
        },
      }
    },
  },
  web: { req: voidFn, res: voidFn },
}

// 웹->앱 authkey 저장
const handleReqSaveAuthKey = {
  aos: (authKey: string) => {
    try {
      window.SaturnAosInterface.handleSendAuthKeyToApp(authKey)
    } catch (e) {
      console.warn(`웹->앱 authkey 저장 요청 실패: ${e}`)
    }
  },
  ios: (authKey: string) =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage(
      `handleSendAuthKeyToApp('${authKey}')`,
    ),
  web: voidFn,
}

// 앱에 저장된 인포(전화번호, authkey) 삭제
const handleReqDeleteInfo = {
  aos: () => {
    try {
      window.SaturnAosInterface.reqDeleteInfo()
    } catch (e) {
      console.warn(`앱에 저장된 인포 삭제 요청 실패: ${e}`)
    }
  },
  ios: () => window.webkit.messageHandlers.SaturnIosInterface.postMessage(`reqDeleteInfo()`),
  web: voidFn,
}

// 앱에서 카카오톡 공유하기 실행
const handleReqGiftKakaoShare = {
  aos: voidFn,
  ios: (data: string) =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage(`reqGiftKakaoShare(${data})`),
  web: voidFn,
}

// 앱에서 시스템 공유하기 실행
const handleReqSystemShare = {
  aos: (data: string) => {
    try {
      window.SaturnAosInterface.reqSystemShare(data)
    } catch (e) {
      console.warn(`시스템 공유 실행 실패: ${e}`)
    }
  },
  ios: (data: string) =>
    window.webkit.messageHandlers.SaturnIosInterface.postMessage(`reqSystemShare(${data})`),
  web: voidFn,
}

export const Bridge = {
  getPushToken: withBridgeTracking(getPushToken[channel], 'getPushToken'),
  getAppInfo: withBridgeTracking(getAppInfo[channel], 'getAppInfo'),
  handleReqExternalOpenUrl: withBridgeTracking(
    handleReqExternalOpenUrl[channel],
    'handleReqExternalOpenUrl',
  ),
  handleReqShowToast: withBridgeTracking(handleReqShowToast[channel], 'handleReqShowToast'),
  handleReqSaveImage: withBridgeTracking(handleReqSaveImage[channel], 'handleReqSaveImage'),
  getUploadImage: withBridgeTracking(getUploadImage[channel], 'getUploadImage', {
    validate: false,
  }),
  handleReqAppClose: withBridgeTracking(handleReqAppClose[channel], 'handleReqAppClose'),
  handleReqUpDisplayLight: withBridgeTracking(
    handleReqUpDisplayLight[channel],
    'handleReqUpDisplayLight',
  ),
  handleReqDownDisplayLight: withBridgeTracking(
    handleReqDownDisplayLight[channel],
    'handleReqDownDisplayLight',
  ),
  handleReqSystemNotification: withBridgeTracking(
    handleReqSystemNotification[channel],
    'handleReqSystemNotification',
  ),
  getLocationPermissionShow: withBridgeTracking(
    getLocationPermissionShow[channel],
    'getLocationPermissionShow',
  ),
  handleReqLocationPermission: withBridgeTracking(
    handleReqLocationPermission[channel],
    'handleReqLocationPermission',
  ),
  handleReqSystemLocation: withBridgeTracking(
    handleReqSystemLocation[channel],
    'handleReqSystemLocation',
  ),
  getLocationInfo: withBridgeTracking(getLocationInfo[channel], 'getLocationInfo'),
  getLocationPermission: withBridgeTracking(
    getLocationPermission[channel],
    'getLocationPermission',
  ),
  handleReqNotification: withBridgeTracking(
    handleReqNotification[channel],
    'handleReqNotification',
  ),
  handleWebViewStartUrl: withBridgeTracking(
    handleWebViewStartUrl[channel],
    'handleWebViewStartUrl',
    { validate: false },
  ),
  handleReqEntryEasyLogin: withBridgeTracking(
    handleReqEntryEasyLogin[channel],
    'handleReqEntryEasyLogin',
  ),
  handleReqSetEasyLogin: withBridgeTracking(
    handleReqSetEasyLogin[channel],
    'handleReqSetEasyLogin',
  ),
  handleReqSignUpLogin: withBridgeTracking(handleReqSignUpLogin[channel], 'handleReqSignUpLogin', {
    validate: false,
  }),
  handleReqSaveName: withBridgeTracking(handleReqSaveName[channel], 'handleReqSaveName'),
  getUserName: withBridgeTracking(getUserName[channel], 'getUserName'),
  receiveResetPinSession: receiveResetPinSession[channel],
  getBiometricAgreeState: withBridgeTracking(
    getBiometricAgreeState[channel],
    'getBiometricAgreeState',
    { validate: false },
  ),
  handleBiometricAgreeState: withBridgeTracking(
    handleBiometricAgreeState[channel],
    'handleBiometricAgreeState',
  ),
  handleReqBiometricAuth: withBridgeTracking(
    handleReqBiometricAuth[channel],
    'handleReqBiometricAuth',
  ),
  getPNumber: withBridgeTracking(getPNumber[channel], 'getPNumber', { validate: false }),
  getContactPermission: withBridgeTracking(getContactPermission[channel], 'getContactPermission'),
  handleReqContactPermission: withBridgeTracking(
    handleReqContactPermission[channel],
    'handleReqContactPermission',
  ),
  handleContactPicker: withBridgeTracking(handleContactPicker[channel], 'handleContactPicker', {
    validate: false,
  }),
  getAuthKey: withBridgeTracking(getAuthKey[channel], 'getAuthKey', { validate: false }),
  handleReqSaveAuthKey: withBridgeTracking(handleReqSaveAuthKey[channel], 'handleReqSaveAuthKey'),
  handleReqDeleteInfo: withBridgeTracking(handleReqDeleteInfo[channel], 'handleReqDeleteInfo'),
  handleReqGiftKakaoShare: withBridgeTracking(
    handleReqGiftKakaoShare[channel],
    'handleReqGiftKakaoShare',
  ),
  handleReqSystemShare: withBridgeTracking(handleReqSystemShare[channel], 'handleReqSystemShare'),
}

//곧 없어짐
export async function requestSelectFile(
  webCallBackFn: () => void,
): Promise<{ fileName: string; base64File: string }> {
  if (ios) {
    return new Promise((resolve, reject) => {
      const clickHandler = function () {
        window.SaturnIosInterface = {
          ...window.SaturnIosInterface,
          receiveSelectedFile: (fileName: string, base64File: string) => {
            resolve({ fileName, base64File })
          },
        }
        window.removeEventListener('click', clickHandler)
      }
      window.addEventListener('click', clickHandler)
      try {
        window.webkit.messageHandlers.SaturnIosInterface.postMessage('selectFile')
      } catch (error) {
        reject('Error selecting image: ' + error)
      }
    })
  } else if (aos) {
    return new Promise((resolve, reject) => {
      const clickHandler = function () {
        window.SaturnWebInterface = {
          ...window.SaturnWebInterface,
          handleSelectedFile: (fileName: string, base64File: string) =>
            resolve({ fileName, base64File }),
        }
        window.removeEventListener('click', clickHandler)
      }
      window.addEventListener('click', clickHandler)
      try {
        window.SaturnAosInterface.selectFile()
      } catch (error) {
        reject('Error selecting file: ' + error)
      }
    })
  } else {
    webCallBackFn()
    return { fileName: '', base64File: '' }
  }
}
